/**
 * Types pour l'éditeur de KPIs custom.
 * Doivent rester alignés avec les DTOs backend
 * (apps/client_api/src/v1/dto/custom_stats/custom_kpi.dto.ts).
 */

export type AggregationType =
  | 'count'
  | 'count_distinct'
  | 'sum'
  | 'avg'
  | 'min'
  | 'max';

export type ChartType =
  | 'kpi'
  | 'table'
  | 'donut'
  | 'pie'
  | 'bar'
  | 'line'
  | 'radar';

export type SourceType = 'custom' | 'system';

export type FilterOperator =
  | 'eq'
  | 'neq'
  | 'gt'
  | 'gte'
  | 'lt'
  | 'lte'
  | 'in'
  | 'not_in'
  | 'contains'
  | 'starts_with'
  | 'ends_with'
  | 'is_null'
  | 'is_not_null';

export type KpiStatus = 'backfilling' | 'active' | 'error';

export interface KpiFilter {
  field: string;
  operator: FilterOperator;
  value?: string | number | boolean | string[] | number[];
}

export type KpiMeasureFormat = 'number' | 'currency' | 'percent' | 'integer';

export interface KpiMeasure {
  id_measure?: string;
  type: AggregationType;
  field?: string;
  format?: KpiMeasureFormat;
  suffix?: string | null;
  label: Record<string, string>;
  position?: number;
}

export interface KpiDimension {
  id_dimension?: string;
  field: string;
  label: Record<string, string>;
  position?: number;
}

export interface CustomKpiDefinition {
  id_custom_kpi: string;
  id_shop: number;
  name: Record<string, string>;
  description?: Record<string, string>;
  measures: KpiMeasure[];
  dimensions: KpiDimension[];
  source_type: SourceType;
  source_schema_name: string;
  source_table_name: string;
  relations_chain?: string[];
  time_field: string;
  time_field_explicit: boolean;
  filters_json?: KpiFilter[];
  aggregate_table_name: string;
  aggregate_procedure_name: string;
  aggregate_event_name: string;
  status: KpiStatus;
  lookback_max_months: number;
  last_event_run_at?: string;
  last_error?: string;
  created_at: string;
  updated_at: string;
}

export interface CreateCustomKpiPayload {
  name: Record<string, string>;
  description?: Record<string, string>;
  measures: KpiMeasure[];
  dimensions?: KpiDimension[];
  source_type: SourceType;
  source_schema_name: string;
  relations_chain?: string[];
  time_field: string;
  time_field_explicit?: boolean;
  filters?: KpiFilter[];
  lookback_max_months?: number;
}

export interface UpdateCustomKpiPayload {
  name?: Record<string, string>;
  description?: Record<string, string>;
  measures?: KpiMeasure[];
  dimensions?: KpiDimension[];
  filters?: KpiFilter[];
}

export interface PreviewKpiPayload extends CreateCustomKpiPayload {
  preview_days?: number;
}

export interface SourceRelationshipInfo {
  propertyName: string;
  targetSchema: string;
  type: 'one-to-many' | 'many-to-one';
}

export interface AvailableSource {
  type: SourceType;
  schemaName: string;
  label: string;
  tableFqn: string;
  relationships?: SourceRelationshipInfo[];
}

export interface AvailableField {
  path: string;
  alias: string;
  column: string;
  label: string;
  type: string; // 'bool' | 'text' | 'number' | 'decimal' | 'list' | 'date' | 'datetime' | ...
  source: 'main' | 'relation';
  relationPath?: string[];
  /**
   * Cardinality de la relation depuis la source principale (uniquement si
   * source='relation').
   * - 'one' (many-to-one) : utilisable en mesure / dimension / filtre.
   * - 'many' (one-to-many) : utilisable **uniquement comme filtre** — le
   *   backend transforme les filtres ciblant cette relation en EXISTS pour
   *   éviter la duplication des rows main. Les agrégations / GROUP BY sont
   *   rejetées par la validation backend.
   */
  relationCardinality?: 'one' | 'many';
}

export interface PreviewResult {
  rows: Record<string, unknown>[];
  rowCount: number;
  sql: string;
  previewDays: number;
  /**
   * Nombre de rows main scannées dans la fenêtre preview. Permet d'estimer
   * le coût d'une procédure backfill (× 365/previewDays). `null` si la
   * mesure a échoué côté backend (non bloquant).
   */
  sourceRowsScanned?: number | null;
  /**
   * Durée d'exécution du SELECT de preview en millisecondes. Permet
   * d'estimer la santé du KPI (warning si le preview prend > 1 s).
   */
  durationMs?: number;
}

// Mapping agrégation × type de champ (mirror du backend AGGREGATIONS_BY_FIELD_TYPE)
export const AGGREGATIONS_BY_FIELD_TYPE: Record<string, AggregationType[]> = {
  bool: ['count', 'count_distinct'],
  text: ['count', 'count_distinct'],
  longtext: ['count'],
  number: ['count', 'count_distinct', 'sum', 'avg', 'min', 'max'],
  decimal: ['count', 'count_distinct', 'sum', 'avg', 'min', 'max'],
  list: ['count', 'count_distinct'],
  date: ['count', 'count_distinct', 'min', 'max'],
  datetime: ['count', 'count_distinct', 'min', 'max'],
  json: [],
  geolocation: [],
};

export const DIMENSION_ELIGIBLE_TYPES = [
  'bool',
  'text',
  'number',
  'decimal',
  'list',
  'date',
  'datetime',
];

export const TIME_FIELD_ELIGIBLE_TYPES = ['date', 'datetime'];

export const ALL_CHART_TYPES: ChartType[] = [
  'kpi',
  'table',
  'donut',
  'pie',
  'bar',
  'line',
  'radar',
];

export const ALL_AGGREGATION_TYPES: AggregationType[] = [
  'count',
  'count_distinct',
  'sum',
  'avg',
  'min',
  'max',
];

export const ALL_FILTER_OPERATORS: FilterOperator[] = [
  'eq',
  'neq',
  'gt',
  'gte',
  'lt',
  'lte',
  'in',
  'not_in',
  'contains',
  'starts_with',
  'ends_with',
  'is_null',
  'is_not_null',
];

export const MAX_RELATION_DEPTH = 3;
export const MAX_FILTERS = 5;
export const MAX_MEASURES_PER_KPI = 8;
export const MAX_DIMENSIONS_PER_KPI = 6;
export const MAX_LOOKBACK_MONTHS = 24;
export const DEFAULT_LOOKBACK_MONTHS = 12;
